import Button from 'devextreme-react/button';
import { useCallback, useEffect, useState } from 'react';
import { listMounts, type MountSummary } from '../fileBrowser/fileBrowserApi';
import {
  StorageFileOpenPickerPopup,
  type StorageFileOpenPickerSelection
} from '../fileBrowser/StorageFileOpenPickerPopup';
import './myAppsFileLaunch.css';

export interface MyAppsFileLaunchNewFile {
  label: string;
  /** Only called when the user has at least one ReadWrite mount; the list is never empty. */
  create: (writableMounts: readonly MountSummary[]) => Promise<void> | void;
}

export interface MyAppsFileLaunchPageProps {
  title: string;
  description: string;
  /** Popup heading, for example "Open PDF". */
  pickerTitle: string;
  accept: (fileName: string) => boolean;
  onOpen: (selection: StorageFileOpenPickerSelection) => void;
  /** A New blank file action, shown only when the user can write somewhere. */
  newFile?: MyAppsFileLaunchNewFile;
}

/**
 * Entry point for My Apps editor and viewer tiles. The site's own /files editor
 * routes need a mountId and path, so a tile has to pick a file first.
 */
export function MyAppsFileLaunchPage({
  title,
  description,
  pickerTitle,
  accept,
  onOpen,
  newFile
}: MyAppsFileLaunchPageProps) {
  const [pickerOpen, setPickerOpen] = useState(true);
  const [writableMounts, setWritableMounts] = useState<readonly MountSummary[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!newFile) return;
    let cancelled = false;
    void listMounts()
      .then((mounts) => {
        if (!cancelled) setWritableMounts(mounts.filter((mount) => mount.mode === 'ReadWrite'));
      })
      .catch(() => {
        if (!cancelled) setWritableMounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [newFile]);

  const handleOpen = useCallback(
    (selection: StorageFileOpenPickerSelection) => {
      setPickerOpen(false);
      onOpen(selection);
    },
    [onOpen]
  );

  const handleCreate = useCallback(async () => {
    if (!newFile || writableMounts.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      await newFile.create(writableMounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the file.');
    } finally {
      setCreating(false);
    }
  }, [newFile, writableMounts]);

  const createLabel = writableMounts.length > 0 ? newFile?.label : undefined;

  return (
    <div className="ops-page my-apps-file-launch">
      <div className="dashboard-header">
        <div>
          <h2 className="dashboard-title">{title}</h2>
          <p className="dashboard-subtitle">{description}</p>
        </div>
      </div>

      {error ? <p className="my-apps-file-launch__error">{error}</p> : null}

      <div className="my-apps-file-launch__actions">
        <Button
          text="Choose a file"
          type="default"
          disabled={creating}
          onClick={() => setPickerOpen(true)}
        />
        {createLabel ? (
          <Button
            text={creating ? 'Creating…' : createLabel}
            stylingMode="outlined"
            disabled={creating}
            onClick={() => void handleCreate()}
          />
        ) : null}
      </div>

      <StorageFileOpenPickerPopup
        visible={pickerOpen}
        title={pickerTitle}
        accept={accept}
        onClose={() => setPickerOpen(false)}
        onOpen={handleOpen}
      />
    </div>
  );
}
